import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  TextField,
  SkeletonBodyText,
  EmptyState,
  Box,
  InlineStack,
  Pagination,
  Text,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { Rental } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function ClinicNotReturned() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-not-returned', page, search],
    queryFn: () => clinicPortalApi.getNotReturnedOrders({ page, limit, search: search || undefined }),
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const rows = (data?.data || []).map((rental: Rental) => [
    orderNumber(rental),
    rental.patientName || 'N/A',
    rental.items?.[0]?.productName || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    <Badge key={rental.id} tone="warning">{rental.rentalStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</Badge>,
    rental.patientPhone || 'N/A',
  ]);

  if (error) {
    return (
      <Page title="Not Returned Orders">
        <Card>
          <EmptyState
            heading="Error loading report"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Not Returned Orders">
      <Card padding="0">
        <Box padding="400">
          <TextField
            label=""
            labelHidden
            placeholder="Search by order number or patient..."
            value={search}
            onChange={handleSearchChange}
            clearButton
            onClearButtonClick={() => handleSearchChange('')}
            autoComplete="off"
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={8} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No not-returned orders"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>All orders have been returned or are within their rental period.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Order #', 'Patient Name', 'Product', 'Start Date', 'End Date', 'Status', 'Phone']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} orders
          </Text>
        </Box>
      )}
    </Page>
  );
}
